import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../assets/images/arka-saydam-logo.png';
import { API_BASE_URL } from '../config';
import { promptKitchenReset } from '../utils/kitchenReset';
import RegistrationSurveyFields from '../components/RegistrationSurveyFields';
import {
  buildRegistrationIntakeSurveyPayload,
  normalizeRegistrationSurveyAnswers,
  REGISTRATION_SURVEY_QUESTIONS
} from '../utils/registrationSurveyModel';

const LUNCH_MODE_OPTIONS = [
  {
    value: 'light_practical_lunch',
    label: 'Hafif/pratik öğlen öner',
    description: 'Yan yemek havuzundan hafif ve hızlı öğlen önerileri üretirim.'
  },
  {
    value: 'extra_dinner_portion',
    label: 'Akşam yemeğini fazla porsiyon planla',
    description: 'Öğleni, akşamdan artacak ek porsiyonla çözerim.'
  },
  {
    value: 'separate_lunch_plan',
    label: 'Öğlen için ayrı yemek öner',
    description: 'Akşamdan bağımsız ayrı bir öğlen planı oluştururum.',
    badge: 'Yüksek Maliyet'
  },
  {
    value: 'leftover_only',
    label: 'Öğlen önerme, kalanlardan yerim',
    description: 'Ayrı menü açmam, yalnızca yumuşak bir hatırlatma bırakırım.'
  }
];

const LUNCH_SERVING_OPTIONS = [
  { value: 1, label: '1 kişi' },
  { value: 2, label: '2 kişi' },
  { value: 3, label: '3 kişi' },
  { value: 4, label: '4 kişi' },
  { value: 'family_size', label: 'Aile kişi sayısı kadar' }
];

function isPartnerFamilyMember(member = {}) {
  const type = String(member?.type || '').trim().toLowerCase();
  const role = String(member?.role || '').trim().toLowerCase();
  return type === 'partner' || role === 'partner';
}

function normalizeFamilyMembers(members = []) {
  if (!Array.isArray(members)) return [];
  return members.map((member) => {
    if (!member || typeof member !== 'object') return member;
    if (!isPartnerFamilyMember(member)) return member;
    return {
      ...member,
      type: 'partner',
      role: 'partner'
    };
  });
}

const ProfilePage = ({ isDarkMode, toggleDarkMode }) => {

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [user, setUser] = useState({ name: '', email: '' });
  const [answers, setAnswers] = useState({ preferences: [], servingSize: 2, frequency: '3-4 Gün', cookingDays: 3 });
  const [templates, setTemplates] = useState([]);
  const [avatar, setAvatar] = useState(null);
  const [babyInfo, setBabyInfo] = useState({ hasBaby: false, babyBirthDate: null, babyAgeGroup: null, babyName: null });
  const [children, setChildren] = useState([]);
  const fileRef = useRef(null);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwOpen, setPwOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [isAssistantChef, setIsAssistantChef] = useState(false);
  const [lunchPreferenceMode, setLunchPreferenceMode] = useState('light_practical_lunch');
  const [lunchServingSelection, setLunchServingSelection] = useState('family_size');
  const [savingLunchPreference, setSavingLunchPreference] = useState(false);
  const [lunchPreferenceSuccess, setLunchPreferenceSuccess] = useState('');
  const navigate = useNavigate();

  const authHeaders = useMemo(() => {
    const t = localStorage.getItem('authToken');
    return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  }, []);

  // [Kullanıcı Sıfırlama] Ortak modal util (3 seçenek + 2. onay + endpoint).
  const handleResetKitchen = () => promptKitchenReset();
  const lunchServingMeaningText = useMemo(() => {
    if (lunchPreferenceMode === 'extra_dinner_portion') {
      return 'Bu sayı akşam yemeğine eklenecek öğlen porsiyonu anlamına gelir.';
    }
    if (lunchPreferenceMode === 'separate_lunch_plan') {
      return 'Bu sayı bağımsız öğlen planının kişi sayısı olarak kullanılacak.';
    }
    if (lunchPreferenceMode === 'leftover_only') {
      return 'Bu sayı stok düşümü yapmaz; yalnızca öğlen rutininin kişi ölçeğini anlamamı sağlar.';
    }
    return 'Bu sayı hafif/pratik öğlen önerilerinin porsiyon hedefi olarak kullanılacak.';
  }, [lunchPreferenceMode]);

  // No auto-redirect so user can toggle off but still see/edit data

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // load profile intakeSurvey first
        const API_URL = API_BASE_URL;
        const pr = await fetch(`${API_URL}/api/profile`, { credentials: 'include', headers: authHeaders });
        let profileData = null;
        if (!mounted) return;
        if (pr.ok) {
          profileData = await pr.json();
          console.log('api/profile response', profileData);
          if (profileData && profileData.isAssistantChef) setIsAssistantChef(true);
          // if profile exists and contains user info, use it to prefill name/email
          if (profileData && profileData.user) {
            setUser({ name: profileData.user.name || '', email: profileData.user.email || '' });
          }
          if (profileData && profileData.intakeSurvey) {
            const savedAnswers = profileData.intakeSurvey.answers || {};
            const normalizedAnswers = normalizeRegistrationSurveyAnswers(profileData.intakeSurvey);

            // Use readable answers if available (backend converts q1->Rutininiz etc)
            const displayAnswers = profileData.intakeSurvey.readableAnswers || normalizedAnswers;

            setAnswers(prev => ({
              ...prev,
              ...normalizedAnswers, // Keep normalized keys for UI
              displayAnswers: displayAnswers, // Add readable answers for display
              // keep a nested copy too so older rendering logic can read `answers.answers[key]`
              answers: savedAnswers,
              preferences: profileData.intakeSurvey.preferences || normalizedAnswers.preferences || savedAnswers.preferences || prev.preferences || []
            }));

            console.log('🔤 Frontend received answers:', {
              original: Object.keys(normalizedAnswers).slice(0, 3),
              readable: Object.keys(displayAnswers).slice(0, 3)
            });

            if (profileData.intakeSurvey.avatar) setAvatar(profileData.intakeSurvey.avatar);
          }
          const savedLunchPreference = profileData?.onboardingStates?.lunchPreference || null;
          if (savedLunchPreference?.mode) {
            setLunchPreferenceMode(savedLunchPreference.mode);
            setLunchServingSelection(savedLunchPreference.selectedServingSize || 'family_size');
          }
          // Load baby information
          if (profileData && (profileData.hasBaby || profileData.babyAgeGroup || profileData.babyName)) {
            const birthDate = profileData.babyBirthDate ? profileData.babyBirthDate.substring(0, 10) : null;
            setBabyInfo({
              hasBaby: profileData.hasBaby || false,
              babyBirthDate: birthDate,
              babyAgeGroup: profileData.babyAgeGroup || computeAgeGroup(birthDate),
              babyName: profileData.babyName || null
            });
          }
          // Load children information
          let loadedChildren = [];
          if (profileData && profileData.children) {
            try {
              const childrenData = typeof profileData.children === 'string' ? JSON.parse(profileData.children) : profileData.children;
              if (Array.isArray(childrenData)) loadedChildren = normalizeFamilyMembers(childrenData);
            } catch (e) {
              console.warn('Failed to parse children data', e);
            }
          }

          // Inject partner if exists in DB but not in children array
          if (profileData && profileData.partnerId && !loadedChildren.some((c) => isPartnerFamilyMember(c))) {
            loadedChildren.push({
              type: 'partner',
              role: 'partner',
              name: profileData.partnerName || 'Eşiniz',
              photo: profileData.partnerAvatar || null,
              id: profileData.partnerId,
              diet: 'Belirtilmedi',
              allergies: []
            });
          }

          setChildren(loadedChildren);

          // if profile doesn't have intakeSurvey saved, try loading the survey table
          if (!profileData?.intakeSurvey) {
            try {
              const sv = await fetch(`${API_URL}/api/survey`, { credentials: 'include', headers: authHeaders });
              if (sv.ok) {
                const sj = await sv.json();
                console.log('api/survey fallback', sj);
                if (sj && sj.answers) {
                  const a = sj.answers;
                  setAnswers(prev => ({ ...prev, ...(a || {}), preferences: a.preferences || a.diet || prev.preferences || [] }));
                }
              }
            } catch (e) {
              console.warn('failed to load survey fallback', e);
            }
          }
        }
        // also try to fill name/email from auth/me (returns user object directly)
        const me = await fetch(`${API_URL}/auth/me`, { credentials: 'include', headers: authHeaders });
        if (me.ok) {
          const jm = await me.json();
          console.log('auth/me', jm);
          if (jm) setUser({ name: jm.name || '', email: jm.email || '' });
        }
        // fetch templates for rendering questions; if unauthorized or none, build a fallback from profile.answers
        try {
          const tres = await fetch(`${API_URL}/api/surveys/templates`, { credentials: 'include', headers: authHeaders });
          if (tres.ok) {
            const tj = await tres.json().catch(() => ({}));
            const fetchedTemplates = Array.isArray(tj.templates) ? tj.templates : [];
            console.log('fetched templates', fetchedTemplates);
            // If profile has answers stored under different keys (like q3) but template uses different ids (like "serving"),
            // map any stored answer values that match a template option into the template question id so the UI shows the correct value.
            if (profileData && profileData.intakeSurvey && profileData.intakeSurvey.answers && fetchedTemplates.length) {
              const stored = normalizeRegistrationSurveyAnswers(profileData.intakeSurvey);
              const mapped = {};
              for (const tpl of fetchedTemplates) {
                const qs = tpl && tpl.questions ? tpl.questions : [];
                for (const q of qs) {
                  if (!q || !q.options || !q.options.length) continue;
                  // find any stored key whose value matches one of q.options
                  for (const [k, v] of Object.entries(stored)) {
                    if (v === undefined || v === null) continue;
                    const isArray = Array.isArray(v);
                    const opts = q.options.map(o => (typeof o === 'string' ? o : (o.label || o.value)));
                    if (isArray) {
                      const overlap = v.some(x => opts.includes(x));
                      if (overlap) mapped[q.id] = v;
                    } else {
                      if (opts.includes(v)) mapped[q.id] = v;
                    }
                    if (mapped[q.id]) break;
                  }
                }
              }
              // apply mapped keys into answers so UI will render them under template question ids
              const merged = Object.assign({}, stored, mapped);
              console.log('mapped answers for template rendering', { stored, mapped, merged });
              setAnswers(prev => ({ ...prev, ...merged, answers: stored, preferences: profileData.intakeSurvey.preferences || stored.preferences || prev.preferences || [] }));
            }
            setTemplates(fetchedTemplates);
          } else if (tres.status === 401 || tres.status === 403) {
            // not authorized to list templates from this session (typical for non-admin users)
            // build a synthetic template from profile answers so UI can render questions
            console.log('templates list unauthorized or forbidden; building fallback from profile', tres.status);
            if (profileData && profileData.intakeSurvey) {
              const ans = normalizeRegistrationSurveyAnswers(profileData.intakeSurvey);
              console.log('profile answers for fallback', ans);
              const qs = REGISTRATION_SURVEY_QUESTIONS.map((q) => ({
                ...q,
                profileKey: q.id,
                options: Array.isArray(q.options)
                  ? Array.from(new Set([...(q.options || []), ...(Array.isArray(ans[q.id]) ? ans[q.id] : [ans[q.id]].filter(Boolean))]))
                  : q.options
              }));
              setTemplates([{ id: 'fallback-from-profile', key: 'registration-fallback', title: 'Kayıt Anketi', questions: qs, activeForNewUsers: false }]);
              setAnswers(prev => ({ ...prev, ...ans, answers: ans, preferences: ans.q5 || ans.preferences || prev.preferences || [] }));
            } else {
              setTemplates([{ id: 'fallback-default', key: 'registration-default', title: 'Kayıt Anketi', questions: REGISTRATION_SURVEY_QUESTIONS, activeForNewUsers: false }]);
            }
          }
        } catch (e) { /* ignore */ }
      } catch (e) {
        console.error('profile load', e);
        setError('Veriler yüklenemedi');
      } finally {
        mounted && setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setAvatar(reader.result);
    reader.readAsDataURL(file);
  };
  const triggerFile = () => fileRef.current && fileRef.current.click();

  const handleChange = (field, value) => setAnswers(a => ({ ...a, [field]: value }));

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const intakeSurvey = buildRegistrationIntakeSurveyPayload(answers);
      if (avatar) intakeSurvey.avatar = avatar;
      const body = {
        intakeSurvey,
        user: { name: user.name, email: user.email },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        // Bebek bilgilerini de ekle
        hasBaby: babyInfo.hasBaby,
        babyAgeGroup: babyInfo.babyAgeGroup,
        babyBirthDate: babyInfo.babyBirthDate,
        babyName: babyInfo.babyName,
        // Ensure children photos are preserved/updated
        children: children.map(c => ({
          ...c,
          role: c.role || c.type, // Ensure role is present for Kitchen Planner consistency
          photo: c.photo || null
        }))
      };

      const API_URL = API_BASE_URL;
      const res = await fetch(`${API_URL}/api/profile`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders }, credentials: 'include', body: JSON.stringify(body) });
      if (!res.ok) throw new Error('save_failed');
      // refresh auth/me to pick up updated User.name for other pages
      try {
        const meRes = await fetch(`${API_URL}/auth/me`, { credentials: 'include', headers: authHeaders });
        if (meRes.ok) {
          const me = await meRes.json();
          if (me) setUser({ name: me.name || '', email: me.email || '' });
        }
      } catch (e) { /* ignore */ }

      setSaving(false);
      setSaveSuccess('Kaydedildi');

      // Dispatch custom event to notify TopBar and others to refresh data
      window.dispatchEvent(new Event('profileUpdated'));

      setTimeout(() => setSaveSuccess(''), 3000);
    } catch (e) {
      console.error('save profile', e);
      setError('Kaydetme başarısız oldu');
      setSaving(false);
    }
  };

  const handleLunchPreferenceSave = async () => {
    setLunchPreferenceSuccess('');
    setError('');

    try {
      setSavingLunchPreference(true);
      const API_URL = API_BASE_URL;
      const res = await fetch(`${API_URL}/api/profile/onboarding/lunch-preference`, {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
        body: JSON.stringify({
          mode: lunchPreferenceMode,
          selectedServingSize: lunchServingSelection,
          extraServingSize: lunchPreferenceMode === 'extra_dinner_portion' ? lunchServingSelection : null,
          source: 'profile_settings',
          canChangeLater: true,
          lunchDecisionTrace: {
            mode: lunchPreferenceMode,
            candidateSource: lunchPreferenceMode === 'light_practical_lunch' ? 'service_type=yan_yemek' : null,
            dinnerDuplicationPrevented: lunchPreferenceMode !== 'extra_dinner_portion',
            servingSize: lunchServingSelection
          }
        })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.error || 'lunch_preference_save_failed');
      }

      setLunchPreferenceSuccess('Öğlen tercihi güncellendi');
      window.dispatchEvent(new Event('profileUpdated'));
      setTimeout(() => setLunchPreferenceSuccess(''), 3000);
    } catch (saveError) {
      console.error('save lunch preference', saveError);
      setError('Öğlen tercihi kaydedilemedi');
    } finally {
      setSavingLunchPreference(false);
    }
  };

  function computeAgeGroup(birthDate) {
    if (!birthDate) return null;
    const start = new Date(birthDate);
    if (Number.isNaN(start.getTime())) return null;
    const now = new Date();
    const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
    if (months < 0) return null;
    if (months < 8) return '6-8';
    if (months < 12) return '8-12';
    if (months < 24) return '12-24';
    return '2+';
  }

  function computeAgeLabel(birthDate) {
    if (!birthDate) return '';
    const start = new Date(birthDate);
    if (Number.isNaN(start.getTime())) return '';
    const now = new Date();
    const months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
    const days = Math.floor((now - start) / (1000 * 60 * 60 * 24));
    const remainingDays = days - Math.floor(days / 30) * 30;
    return `${Math.max(0, months)} ay ${Math.max(0, remainingDays)} gün`;
  }

  // Handle baby birthdate change with auto-save
  const handleBirthDateChange = async (newBirthDate) => {
    const derivedAgeGroup = computeAgeGroup(newBirthDate);
    const updatedBabyInfo = { ...babyInfo, babyBirthDate: newBirthDate, babyAgeGroup: derivedAgeGroup };
    setBabyInfo(updatedBabyInfo);

    try {
      const API_URL = API_BASE_URL;
      const res = await fetch(`${API_URL}/api/profile/update-baby-preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({
          hasBaby: updatedBabyInfo.hasBaby,
          babyAgeGroup: derivedAgeGroup,
          babyBirthDate: newBirthDate,
          babyName: updatedBabyInfo.babyName
        })
      });

      if (res.ok) {
        // Notify TopBar to refresh
        window.dispatchEvent(new Event('profileUpdated'));
      } else {
        console.error('Failed to update baby birthdate');
        setBabyInfo(babyInfo);
      }
    } catch (error) {
      console.error('Error updating baby birthdate:', error);
      setBabyInfo(babyInfo);
    }
  };

  const handleChangePassword = async () => {
    setPwError('');
    setPwSuccess('');
    if (!oldPassword || !newPassword || !confirmPassword) {
      setPwError('Lütfen tüm alanları doldurun');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('Yeni şifreler eşleşmiyor');
      return;
    }
    setPwSaving(true);
    try {
      const API_URL = API_BASE_URL;
      const res = await fetch(`${API_URL}/api/profile/change-password`, {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
        body: JSON.stringify({ oldPassword, newPassword }),
      });
      if (res.ok) {
        setPwSuccess('Şifre başarıyla değiştirildi');
        setOldPassword(''); setNewPassword(''); setConfirmPassword('');
      } else {
        const j = await res.json().catch(() => ({}));
        const msg = j && j.error ? j.error : 'Şifre değiştirme başarısız';
        setPwError(msg);
      }
    } catch (e) {
      console.error('change password', e);
      setPwError('Sunucu hatası');
    } finally {
      setPwSaving(false);
    }
  };

  // (recommended recipes removed — profile page focuses on user data)

  if (loading) return <div className="p-8">Yükleniyor…</div>;

  return (
    <div className="w-full max-w-4xl mx-auto px-2 py-2 sm:px-4 md:px-6 md:py-10">
      <div className="flex flex-col gap-6 bg-card-bg dark:bg-[#09090b] border border-border-light dark:border-white/5 p-5 sm:p-8 lg:p-10 rounded-3xl shadow-xl overflow-hidden relative">
        <div className="h-32 bg-gradient-to-r from-primary/10 via-red-100 to-primary/10 dark:from-primary/5 dark:via-[#3a1515] dark:to-primary/5" />
        <div className="px-0 pb-0 flex flex-col">
          <div className="flex justify-center -mt-20 mb-6">
            <div className="relative flex flex-col items-center">
              <div className="h-32 w-32 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center border border-border-light">
                {avatar ? (
                  <img src={avatar} alt="avatar" className="h-full w-full object-cover" />
                ) : (
                  <img src={Logo} alt="logo" className="h-full w-full object-contain p-3" />
                )}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={e => handleFile(e.target.files && e.target.files[0])} className="hidden" />
                <button type="button" onClick={triggerFile} className="px-4 py-1.5 rounded-lg bg-white/90 dark:bg-white/5 border border-border-light text-sm">Fotoğrafı Değiştir</button>
                {avatar ? <button type="button" onClick={() => setAvatar(null)} className="px-3 py-1.5 rounded-lg text-sm text-red-600">Kaldır</button> : null}
              </div>

              {/* Special Back Button for Assistant Chefs */}
              {isAssistantChef && (
                <button
                  type="button"
                  onClick={() => navigate('/partner-dashboard')}
                  className="mt-4 flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-full text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  <span className="material-symbols-outlined text-sm">arrow_back</span>
                  Panele Dön
                </button>
              )}
            </div>
          </div>

          <form className="space-y-10 max-w-full">
            <div className="flex flex-col">
              <div className="border-b border-gray-200 dark:border-gray-800 mb-6">
                <nav className="flex flex-wrap gap-x-4 gap-y-2" aria-label="Tabs">
                  {/* Use a more persistent check for tab visibility */}
                  {[{ key: 'personal', label: 'Kişisel', icon: 'person' }, { key: 'baby', label: 'Aile Bilgileri', icon: 'child_care', hidden: false /* Always accessible if they once entered data */ }, { key: 'preferences', label: 'Anket', icon: 'quiz' }]
                    .filter(tab => !tab.hidden)
                    .map(tab => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveTab(tab.key)}
                        className={`${activeTab === tab.key
                          ? 'border-primary text-primary bg-primary/5'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-300 dark:hover:text-white'
                          } whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm flex items-center gap-1.5 rounded-t-lg transition-colors`}
                      >
                        <span className="material-symbols-outlined text-[16px]">{tab.icon}</span>
                        <span className="hidden sm:inline">{tab.label}</span>
                      </button>
                    ))}
                </nav>
              </div>

              {/* Personal tab */}
              {activeTab === 'personal' ? (
                <div className="space-y-6">
                  <div className="flex items-center gap-2 pb-2 border-b border-gray-100 dark:border-gray-800">
                    <span className="material-symbols-outlined text-primary">person</span>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Kişisel Bilgiler</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ad Soyad</label>
                      <input value={user.name} onChange={e => setUser(u => ({ ...u, name: e.target.value }))} className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:border-primary focus:ring-primary shadow-sm text-sm py-2.5" type="text" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">E-posta</label>
                      <input value={user.email} onChange={e => setUser(u => ({ ...u, email: e.target.value }))} className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:border-primary focus:ring-primary shadow-sm text-sm py-2.5" type="email" />
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2 pb-2 border-b border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary">lock</span>
                      <h2 className="text-lg font-bold text-gray-900 dark:text-white">Şifre Değişikliği</h2>
                    </div>
                    <button type="button" onClick={() => setPwOpen(p => !p)} className="text-sm text-primary font-medium flex items-center gap-2">
                      <span>{pwOpen ? 'Kapat' : 'Şifremi Değiştir'}</span>
                      <svg className={`w-4 h-4 transition-transform ${pwOpen ? 'rotate-180' : 'rotate-0'}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l5 5a1 1 0 11-1.414 1.414L10 5.414 5.707 9.707A1 1 0 114.293 8.293l5-5A1 1 0 0110 3z" clipRule="evenodd" /></svg>
                    </button>
                  </div>

                  <div className={`transition-all duration-300 ease-in-out overflow-hidden ${pwOpen ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mevcut Şifre</label>
                        <input value={oldPassword} onChange={e => setOldPassword(e.target.value)} className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:border-primary focus:ring-primary shadow-sm text-sm py-2.5" type="password" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Yeni Şifre</label>
                        <input value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:border-primary focus:ring-primary shadow-sm text-sm py-2.5" type="password" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Yeni Şifre (Tekrar)</label>
                        <input value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:border-primary focus:ring-primary shadow-sm text-sm py-2.5" type="password" />
                      </div>
                      {pwError ? <div className="text-sm text-red-600">{pwError}</div> : null}
                      {pwSuccess ? <div className="text-sm text-green-600">{pwSuccess}</div> : null}
                      <div className="flex">
                        <button type="button" onClick={handleChangePassword} disabled={pwSaving} className="ml-auto px-6 py-2.5 rounded-lg bg-primary text-white font-medium shadow-sm hover:bg-primary-dark transition-colors">
                          {pwSaving ? 'Güncelleniyor…' : 'Şifreyi Değiştir'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Preferences tab */}
              {activeTab === 'preferences' ? (
                <div className="mx-auto w-full max-w-2xl space-y-6">
                    <div className="rounded-[26px] border border-sky-100 bg-white p-5 shadow-sm dark:border-sky-900/40 dark:bg-slate-900/40">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-sky-600 dark:text-sky-300">Öğlen Planı</p>
                          <h3 className="mt-1 text-lg font-black text-slate-900 dark:text-white">Öğlen tercihini sonradan değiştirebilirsin</h3>
                          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                            Öğlen bildirimlerin açıksa, mutfak akışının nasıl işleyeceğini buradan güncelleyebilirsin.
                          </p>
                        </div>
                        <span className="material-symbols-outlined text-[28px] text-sky-500 dark:text-sky-300">lunch_dining</span>
                      </div>

                      <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-950/40">
                        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">Öğlen yemeğini kaç kişi için düzenleyeyim?</p>
                        <div className="mt-3 grid gap-2 sm:grid-cols-3">
                          {LUNCH_SERVING_OPTIONS.map((option) => {
                            const selected = lunchServingSelection === option.value;
                            return (
                              <button
                                key={String(option.value)}
                                type="button"
                                onClick={() => setLunchServingSelection(option.value)}
                                className={`rounded-2xl border px-3 py-3 text-left text-sm font-bold transition ${
                                  selected
                                    ? 'border-sky-500 bg-sky-600 text-white shadow-lg shadow-sky-500/20'
                                    : 'border-slate-200 bg-white text-slate-700 hover:border-sky-300 dark:border-slate-700 dark:bg-[#0d1326] dark:text-slate-200'
                                }`}
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                        <p className="mt-3 text-xs font-medium text-slate-500 dark:text-slate-400">{lunchServingMeaningText}</p>
                      </div>

                      <div className="mt-4 grid gap-3">
                        {LUNCH_MODE_OPTIONS.map((option) => {
                          const selected = lunchPreferenceMode === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => setLunchPreferenceMode(option.value)}
                              className={`rounded-[1.4rem] border px-4 py-4 text-left transition ${
                                selected
                                  ? 'border-sky-500 bg-sky-50 shadow-lg shadow-sky-500/10 dark:border-sky-400 dark:bg-sky-900/20'
                                  : 'border-slate-200 bg-white hover:border-sky-200 dark:border-slate-700 dark:bg-[#0b0f1d]'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-sm font-black text-slate-900 dark:text-white">{option.label}</p>
                                  <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">{option.description}</p>
                                </div>
                                {option.badge ? (
                                  <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-red-600 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
                                    <span className="material-symbols-outlined text-[14px]">warning</span>
                                    {option.badge}
                                  </span>
                                ) : null}
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      {lunchPreferenceMode === 'leftover_only' ? (
                        <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-medium text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200">
                          Öğlen için ayrı menü açılmayacak. Saat geldiğinde yalnızca yumuşak bir hatırlatma gönderilecek.
                        </p>
                      ) : null}

                      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                        {lunchPreferenceSuccess ? (
                          <p className="text-sm font-medium text-emerald-600 dark:text-emerald-300">{lunchPreferenceSuccess}</p>
                        ) : (
                          <p className="text-xs text-slate-500 dark:text-slate-400">Bu tercih öğlen akışına hemen uygulanır.</p>
                        )}
                        <button
                          type="button"
                          onClick={handleLunchPreferenceSave}
                          disabled={savingLunchPreference}
                          className="inline-flex items-center gap-2 rounded-2xl bg-sky-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-sky-600/20 transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <span className="material-symbols-outlined text-[18px]">save</span>
                          {savingLunchPreference ? 'Kaydediliyor...' : 'Öğlen Tercihini Kaydet'}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pb-2 border-b border-gray-100 dark:border-gray-800">
                      <span className="material-symbols-outlined text-primary">restaurant_menu</span>
                      <h2 className="text-lg font-bold text-gray-900 dark:text-white">Beslenme Tercihleri (Anket Verileri)</h2>
                    </div>
                      <RegistrationSurveyFields
                        answers={answers}
                        onChange={setAnswers}
                        density="compact"
                        tone="surface-dark"
                        className="rounded-[22px] bg-slate-50/40 dark:bg-zinc-900/20 border border-slate-100 dark:border-zinc-800/40 p-2.5 sm:p-3.5 shadow-sm"
                      />
                </div>
              ) : null}

              {/* Baby Information Tab - Persistent data view */}
              {activeTab === 'baby' ? (
                <div className="space-y-6">
                  <div className="flex items-center gap-2 pb-2 border-b border-gray-100 dark:border-gray-800">
                    <span className="material-symbols-outlined text-primary">child_care</span>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Bebek ve Çocuk Bilgileri</h2>
                  </div>

                  {/* Children & Family Section */}
                  <div className="space-y-6 mb-8">
                    {/* 1. Children Section */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-md font-bold text-gray-800 dark:text-gray-200">Çocuk Bilgileri</h3>
                        <button
                          type="button"
                          onClick={() => setChildren([...children, { type: 'child', name: '', birthDate: '', addedAt: new Date().toISOString() }])}
                          className="text-sm text-primary font-medium flex items-center gap-1"
                        >
                          <span className="material-symbols-outlined text-sm">add</span>
                          Yeni Çocuk Ekle
                        </button>
                      </div>

                      {children.filter((c) => !isPartnerFamilyMember(c)).length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {children.filter((c) => !isPartnerFamilyMember(c)).map((child, idx) => (
                            <div key={idx} className="bg-white dark:bg-white/5 border border-border-light dark:border-white/10 rounded-xl p-4 relative group">
                              <button
                                type="button"
                                onClick={() => setChildren(children.filter((_, i) => i !== idx))}
                                className="absolute top-2 right-2 text-gray-400 hover:text-red-500 transition-colors"
                              >
                                <span className="material-symbols-outlined text-lg">close</span>
                              </button>
                              <div className="flex items-center gap-4 mb-3">
                                <div className="relative group flex-shrink-0">
                                  {child.photo ? (
                                    <img src={child.photo} alt={child.name} className="w-16 h-16 rounded-xl object-cover border-2 border-primary/20" />
                                  ) : (
                                    <div className="w-16 h-16 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center border-2 border-slate-200 dark:border-slate-700 text-slate-400">
                                      <span className="material-symbols-outlined text-2xl">child_care</span>
                                    </div>
                                  )}
                                  <label className="absolute -bottom-2 -right-2 w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-red-700 transition-colors shadow-lg border-2 border-white dark:border-slate-800 scale-0 group-hover:scale-100 transition-transform">
                                    <span className="material-symbols-outlined text-[14px]">photo_camera</span>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          const reader = new FileReader();
                                          reader.onload = (rev) => {
                                            const newChildren = [...children];
                                            const globalIdx = children.indexOf(child);
                                            newChildren[globalIdx].photo = rev.target.result;
                                            setChildren(newChildren);
                                          };
                                          reader.readAsDataURL(file);
                                        }
                                      }}
                                    />
                                  </label>
                                </div>
                                <div className="flex-1 space-y-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Çocuğun İsmi</label>
                                    <input
                                      type="text"
                                      value={child.name || ''}
                                      onChange={(e) => {
                                        const newChildren = [...children];
                                        const globalIdx = children.indexOf(child);
                                        newChildren[globalIdx].name = e.target.value;
                                        setChildren(newChildren);
                                      }}
                                      placeholder="İsim giriniz"
                                      className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white text-sm py-2 px-3"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Doğum Tarihi</label>
                                    <input
                                      type="date"
                                      value={child.birthDate ? child.birthDate.substring(0, 10) : ''}
                                      onChange={(e) => {
                                        const newChildren = [...children];
                                        const globalIdx = children.indexOf(child);
                                        newChildren[globalIdx].birthDate = e.target.value;
                                        setChildren(newChildren);
                                      }}
                                      className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white text-sm py-2 px-3"
                                    />
                                    {child.birthDate && (
                                      <p className="text-[10px] text-gray-500 mt-1">
                                        Yaş: {computeAgeLabel(child.birthDate)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
                          <p className="text-gray-500 text-sm">Henüz çocuk bilgisi eklenmemiş.</p>
                        </div>
                      )}
                    </div>

                    {/* 2. Partner / Other Members Section */}
                    <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                      <h3 className="text-md font-bold text-gray-800 dark:text-gray-200">Diğer Aile Üyeleri (Partner)</h3>
                      {children.filter((c) => isPartnerFamilyMember(c)).length > 0 ? (
                        <div className="grid grid-cols-1 gap-4">
                          {children.filter((c) => isPartnerFamilyMember(c)).map((partner, idx) => (
                            <div key={idx} className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 border border-blue-100 dark:border-blue-900/30 rounded-2xl p-5 flex flex-col sm:flex-row gap-6 relative">
                              <div className="flex-shrink-0 flex flex-col items-center justify-center bg-white dark:bg-slate-800 w-20 h-20 rounded-2xl border border-blue-200 dark:border-blue-800 shadow-sm overflow-hidden">
                                {partner.photo ? (
                                  <img src={partner.photo} alt={partner.name} className="w-full h-full object-cover" />
                                ) : (
                                  <>
                                    <span className="text-2xl">👨‍🍳</span>
                                    <span className="text-[8px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-tighter">PARTNER</span>
                                  </>
                                )}
                              </div>

                              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div>
                                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">İsim & Yakınlık</p>
                                  <p className="text-sm font-bold text-gray-900 dark:text-white">{partner.name}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{partner.relation || 'Yardımcı Şef'}</p>
                                </div>

                                <div>
                                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Beslenme Tercihi</p>
                                  <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                                    {partner.diet || 'Belirtilmedi'}
                                  </p>
                                </div>

                                <div>
                                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Gıda Hassasiyetleri</p>
                                  <div className="flex flex-wrap gap-1">
                                    {partner.allergies && partner.allergies.length > 0 ? (
                                      partner.allergies.map((alg, i) => (
                                        <span key={i} className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-md text-[10px] font-bold">
                                          {alg}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="text-xs text-green-600 dark:text-green-400">Hassasiyet Yok</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
                          <p className="text-gray-500 text-sm">Bağlı bir partner bilgisi bulunamadı.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-gray-100 dark:border-gray-800 pt-6">
                    <h3 className="text-md font-bold text-gray-800 dark:text-gray-200 mb-4">Bebek Güvenlik Sistemi</h3>
                    {(babyInfo.hasBaby || babyInfo.babyName || babyInfo.babyBirthDate) ? (
                      <div className="grid grid-cols-1 gap-4">
                        <div className={`rounded-lg p-6 border transition-colors ${babyInfo.hasBaby ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' : 'bg-gray-50 dark:bg-gray-800/20 border-gray-200 dark:border-gray-700'}`}>
                          <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                              <span className="text-3xl">{babyInfo.hasBaby ? '👶' : '💤'}</span>
                              <span className={`text-lg font-bold ${babyInfo.hasBaby ? 'text-blue-800 dark:text-blue-200' : 'text-gray-500'}`}>
                                Bebek Güvenlik Sistemi {babyInfo.hasBaby ? 'AKTİF' : 'PASİF'}
                              </span>
                            </div>

                            {/* Toggle Switch */}
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={babyInfo.hasBaby}
                                onChange={(e) => setBabyInfo(prev => ({ ...prev, hasBaby: e.target.checked }))}
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                            </label>
                          </div>

                          <div className={`space-y-4 transition-opacity ${babyInfo.hasBaby ? 'opacity-100' : 'opacity-60'}`}>
                            {/* Baby Birthdate */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Bebeğin Doğum Tarihi
                              </label>
                              <input
                                type="date"
                                value={babyInfo.babyBirthDate || ''}
                                max={new Date().toISOString().split('T')[0]}
                                onChange={(e) => handleBirthDateChange(e.target.value || null)}
                                className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:border-primary focus:ring-primary shadow-sm text-sm py-3 px-4"
                              />
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Doğum tarihi değiştirildiğinde otomatik kaydedilir. Yaş grubu otomatik hesaplanır.
                              </p>
                              {babyInfo.babyBirthDate && (
                                <div className="mt-2 text-sm text-pink-700 dark:text-pink-300 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded-lg p-3">
                                  Yaklaşık yaş: {computeAgeLabel(babyInfo.babyBirthDate)}
                                  {babyInfo.babyAgeGroup ? ` • Grup: ${babyInfo.babyAgeGroup}` : ''}
                                </div>
                              )}
                            </div>

                            {/* Baby Name */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Bebek İsmi (İsteğe bağlı)
                              </label>
                              <input
                                type="text"
                                value={babyInfo.babyName || ''}
                                onChange={(e) => setBabyInfo(prev => ({ ...prev, babyName: e.target.value || null }))}
                                placeholder="Bebeğinizin ismini girebilirsiniz..."
                                className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:border-primary focus:ring-primary shadow-sm text-sm py-3 px-4"
                              />
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Bu isim tariflerde kişiselleştirme için kullanılır
                              </p>
                            </div>

                            {/* Help Text */}
                            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                              <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                                * Sistem pasif olduğunda verileriniz korunur ancak bebek ikonları ve koruma kalkanları gizlenir.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                        <span className="text-6xl mb-4 block">👶</span>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          Henüz bebek bilgisi eklenmemiş
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                          Tarif ararken bebek dostu tarifler için bilgi ekleyebilirsiniz.
                        </p>
                        <button
                          onClick={() => setBabyInfo(prev => ({ ...prev, hasBaby: true }))}
                          className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                        >
                          Bebek Bilgisi Ekle
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              <div className="pt-2 sticky bottom-0 bg-white dark:bg-[#09090b] pb-4 sm:pb-0 z-10">
                <div className="flex gap-3">
                  {saveSuccess ? <div className="self-center text-sm text-green-600">{saveSuccess}</div> : null}
                  <button type="button" onClick={handleSave} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-primary text-white font-bold text-lg shadow-lg shadow-primary/30 hover:bg-primary-dark hover:shadow-primary/40 transition-all active:scale-[0.98]">
                    <span className="material-symbols-outlined">save</span>
                    Değişiklikleri Kaydet
                  </button>
                </div>
              </div>

              <div className="mt-8 text-center pb-8 flex flex-col items-center gap-3">
                <button type="button" onClick={handleResetKitchen} className="text-sm font-bold text-amber-600 hover:text-amber-700 transition-colors underline decoration-transparent hover:decoration-amber-600">
                  Mutfağımı Sıfırla
                </button>
                <button type="button" className="text-sm font-medium text-gray-500 hover:text-red-500 transition-colors underline decoration-transparent hover:decoration-red-500">Hesabımı Sil</button>
              </div>
            </div>
          </form>
        </div >
      </div >
    </div >
  );
};

export default ProfilePage;
